import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {

  Search,

  Workflow,

  ChevronRight,

  Save,

  GitCompare,

  Play,

  Rocket,

  Loader2,

  AlertCircle,

  CheckCircle2,

} from 'lucide-react';

import ApprovalDesignerCanvas from './ApprovalDesignerCanvas';

import { MOCK_APPROVAL_RULES } from './approvalDesignerMockData';

import {

  listApprovalDrafts,

  saveApprovalDraft,

  updateApprovalDraft,

  compareApprovalDraft,

  formatDraftApiError,

  simulateApprovalDraft,

  deployApprovalDraft,

  mapDraftToRule,

  dedupeApprovalRules,

} from './approvalDraftApi';



function cloneDesign(design) {

  return design ? JSON.parse(JSON.stringify(design)) : null;

}



function mockRulesToState() {

  return MOCK_APPROVAL_RULES.map((r) => ({

    ...r,

    mongoId: null,

    draftStatus: 'DRAFT',

    design: cloneDesign(r.design),

  }));

}



/**

 * Split-pane Approval Designer: searchable rules list (left) + visual workflow canvas (right).

 * Edits persist to local MongoDB as DRAFT — Oracle deploy is explicit only.

 */

export default function ApprovalDesigner({

  api = null,

  externalDesign = null,

  externalDesignLabel = 'AI generated rule',

  externalPolicyText = '',

  transactionCategory = 'absence',

  pushTargetId = '',

  onDesignChange,

  onDraftSaved,

  onDeployResult,

  onSimulateResult,

  readOnly = false,

}) {

  const [rules, setRules] = useState(mockRulesToState);

  const [selectedRuleId, setSelectedRuleId] = useState(MOCK_APPROVAL_RULES[0]?.id || null);

  const [search, setSearch] = useState('');

  const [draftsLoading, setDraftsLoading] = useState(false);

  const [draftError, setDraftError] = useState('');

  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error

  const [compareResult, setCompareResult] = useState(null);

  const [compareLoading, setCompareLoading] = useState(false);

  const [simulateLoading, setSimulateLoading] = useState(false);

  const [deployLoading, setDeployLoading] = useState(false);

  const [showDeployConfirm, setShowDeployConfirm] = useState(false);

  const saveTimerRef = useRef(null);

  const pendingSaveRef = useRef(null);



  const draftsEnabled = Boolean(api);



  const loadDrafts = useCallback(async () => {

    if (!api) return;

    setDraftsLoading(true);

    setDraftError('');

    try {

      const drafts = dedupeApprovalRules(await listApprovalDrafts(api));

      if (drafts.length > 0) {

        setRules(drafts);

        setSelectedRuleId((prev) => {

          if (prev && drafts.some((d) => d.id === prev)) return prev;

          return drafts[0].id;

        });

      }

    } catch (err) {

      setDraftError(formatDraftApiError(err, 'Failed to load drafts'));

    } finally {

      setDraftsLoading(false);

    }

  }, [api]);



  useEffect(() => {

    loadDrafts();

  }, [loadDrafts]);



  // Merge AI-generated design as a selectable rule (or update existing generated rule).

  useEffect(() => {

    if (!externalDesign?.stages?.length) return;

    const generatedId = 'rule-ai-generated';

    setRules((prev) => {

      const existing = prev.find((r) => r.id === generatedId);

      const entry = {

        id: generatedId,

        mongoId: existing?.mongoId || null,

        name: externalDesignLabel,

        transaction: externalDesign.transactionHint || 'Generated',

        status: 'draft',

        draftStatus: 'DRAFT',

        summary: externalDesign.summary || 'Generated from natural language.',

        design: cloneDesign(externalDesign),

        transactionCategory,

      };

      if (existing) {

        return prev.map((r) => (r.id === generatedId ? { ...existing, ...entry } : r));

      }

      return [entry, ...prev];

    });

    setSelectedRuleId(generatedId);

  }, [externalDesign, externalDesignLabel, transactionCategory]);



  const filteredRules = useMemo(() => {

    const q = search.trim().toLowerCase();

    if (!q) return rules;

    return rules.filter(

      (r) =>

        r.name.toLowerCase().includes(q) ||

        r.transaction.toLowerCase().includes(q) ||

        (r.summary || '').toLowerCase().includes(q)

    );

  }, [rules, search]);



  const selectedRule = rules.find((r) => r.id === selectedRuleId) || null;

  const canvasDesign = selectedRule?.design || null;



  const persistDraft = useCallback(

    async (rule, design) => {

      if (!api || !rule || readOnly) return null;

      setSaveState('saving');

      setDraftError('');

      try {

        const payload = {

          ruleId: rule.id,

          ruleName: rule.name,

          transaction: rule.transaction,

          transactionCategory: rule.transactionCategory || transactionCategory,

          design,

          policyText: rule.id === 'rule-ai-generated' ? externalPolicyText : rule.policyText || '',

          summary: design.summary || rule.summary || '',

        };

        let saved;

        if (rule.mongoId) {

          saved = await updateApprovalDraft(api, rule.mongoId, payload);

        } else {

          saved = await saveApprovalDraft(api, payload);

        }

        setRules((prev) =>

          prev.map((r) =>

            r.id === rule.id

              ? {

                  ...r,

                  mongoId: saved.mongoId,

                  draftStatus: saved.draftStatus,

                  status: saved.status,

                  design: cloneDesign(saved.design),

                }

              : r

          )

        );

        setSaveState('saved');

        onDraftSaved?.(saved);

        return saved;

      } catch (err) {

        const msg = formatDraftApiError(err, 'Draft save failed');

        setDraftError(msg);

        setSaveState('error');

        return null;

      }

    },

    [api, readOnly, transactionCategory, externalPolicyText, onDraftSaved]

  );



  const scheduleDraftSave = useCallback(

    (rule, design) => {

      if (!api || readOnly) return;

      pendingSaveRef.current = { rule, design };

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

      saveTimerRef.current = setTimeout(() => {

        const pending = pendingSaveRef.current;

        if (pending) persistDraft(pending.rule, pending.design);

      }, 900);

    },

    [api, readOnly, persistDraft]

  );



  useEffect(() => {

    return () => {

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    };

  }, []);



  const lastExternalSaveKeyRef = useRef('');

  useEffect(() => {

    if (!api || !externalDesign?.stages?.length) return;

    const key = JSON.stringify({

      stages: externalDesign.stages,

      label: externalDesignLabel,

    });

    if (lastExternalSaveKeyRef.current === key) return;

    lastExternalSaveKeyRef.current = key;

    const rule = rules.find((r) => r.id === 'rule-ai-generated');

    if (!rule) return;

    scheduleDraftSave(rule, rule.design);

  }, [api, externalDesign, externalDesignLabel, rules, scheduleDraftSave]);



  const handleRuleDesignChange = useCallback(

    (nextDesign) => {

      if (!selectedRuleId) return;

      setRules((prev) => {

        const next = prev.map((r) =>

          r.id === selectedRuleId

            ? { ...r, design: cloneDesign(nextDesign), status: 'draft', draftStatus: 'DRAFT' }

            : r

        );

        const rule = next.find((r) => r.id === selectedRuleId);

        if (rule) scheduleDraftSave(rule, nextDesign);

        return next;

      });

      if (selectedRuleId === 'rule-ai-generated') {

        onDesignChange?.(nextDesign);

      }

      setCompareResult(null);

    },

    [selectedRuleId, onDesignChange, scheduleDraftSave]

  );



  async function handleManualSave() {

    if (!selectedRule) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    await persistDraft(selectedRule, selectedRule.design);

  }



  async function handleCompare() {

    if (!selectedRule?.mongoId || !api) {

      setDraftError('Save the rule as a draft first (MongoDB required).');

      return;

    }

    setCompareLoading(true);

    setCompareResult(null);

    setDraftError('');

    try {

      const result = await compareApprovalDraft(api, selectedRule.mongoId);

      setCompareResult(result);

    } catch (err) {

      setDraftError(formatDraftApiError(err, 'Compare failed'));

    } finally {

      setCompareLoading(false);

    }

  }



  async function handleSimulate() {

    if (!selectedRule?.mongoId || !api) {

      setDraftError('Save the rule as a draft first (MongoDB required).');

      return;

    }

    setSimulateLoading(true);

    setDraftError('');

    try {

      const result = await simulateApprovalDraft(api, selectedRule.mongoId, {

        category: selectedRule.transactionCategory || transactionCategory,

      });

      onSimulateResult?.(result);

    } catch (err) {

      setDraftError(err.response?.data?.error || err.message || 'Simulation failed');

    } finally {

      setSimulateLoading(false);

    }

  }



  async function handleDeploy(dryRun = false) {

    if (!selectedRule?.mongoId || !api) {

      setDraftError('Save the rule as a draft first (MongoDB required).');

      return;

    }

    setDeployLoading(true);

    setDraftError('');

    setShowDeployConfirm(false);

    try {

      const result = await deployApprovalDraft(api, selectedRule.mongoId, {

        connectionId: pushTargetId || undefined,

        category: selectedRule.transactionCategory || transactionCategory,

        dryRun,

      });

      if (result.draft) {

        const mapped = mapDraftToRule(result.draft);

        setRules((prev) =>

          prev.map((r) =>

            r.id === selectedRule.id

              ? {

                  ...r,

                  mongoId: mapped.mongoId,

                  draftStatus: mapped.draftStatus,

                  status: mapped.status,

                }

              : r

          )

        );

      }

      onDeployResult?.(result);

    } catch (err) {

      const msg = err.response?.data?.error || err.message || 'Deploy failed';

      setDraftError(msg);

      onDeployResult?.({ ok: false, error: msg, code: err.response?.data?.code });

    } finally {

      setDeployLoading(false);

    }

  }



  const statusBadge = selectedRule

    ? selectedRule.draftStatus === 'PUBLISHED' && selectedRule.status !== 'draft'

      ? { label: 'Published', className: 'bg-emerald-100 text-emerald-800' }

      : { label: 'Draft', className: 'bg-amber-100 text-amber-900' }

    : null;



  return (

    <div className="flex min-h-[480px] flex-col overflow-hidden rounded-lg border border-cyan-200 bg-white shadow-sm lg:min-h-[520px] lg:flex-row">

      {/* Left: searchable rules list */}

      <aside className="flex w-full shrink-0 flex-col border-b border-cyan-100 bg-cyan-50/40 lg:w-72 lg:max-w-[320px] lg:border-b-0 lg:border-r">

        <div className="border-b border-cyan-100 px-3 py-2.5">

          <div className="flex items-center gap-2">

            <Workflow className="h-4 w-4 text-cyan-700" />

            <p className="text-xs font-semibold uppercase tracking-wide text-cyan-900">

              {draftsEnabled ? 'Approval rules' : 'Active rules'}

            </p>

            {draftsLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-600" />}

          </div>

          <div className="relative mt-2">

            <Search className="pointer-events-none absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />

            <input

              type="search"

              value={search}

              onChange={(e) => setSearch(e.target.value)}

              placeholder="Search rules…"

              className="w-full rounded-lg border border-cyan-200 bg-white py-1.5 pl-8 pr-2.5 text-xs text-slate-800 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"

            />

          </div>

        </div>



        <ul className="min-h-0 flex-1 overflow-y-auto divide-y divide-cyan-100/80">

          {filteredRules.length === 0 ? (

            <li className="p-4 text-xs text-slate-500">No rules match your search.</li>

          ) : (

            filteredRules.map((rule) => {

              const active = rule.id === selectedRuleId;

              const isDraft = rule.draftStatus !== 'PUBLISHED' || rule.status === 'draft';

              return (

                <li key={rule.id}>

                  <button

                    type="button"

                    onClick={() => setSelectedRuleId(rule.id)}

                    className={`flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors ${

                      active

                        ? 'bg-cyan-100/90 border-l-2 border-l-cyan-600'

                        : 'hover:bg-cyan-50/80 border-l-2 border-l-transparent'

                    }`}

                  >

                    <div className="min-w-0 flex-1">

                      <div className="flex items-center gap-1.5">

                        <p className="text-sm font-medium text-slate-800 truncate">{rule.name}</p>

                        {isDraft && (

                          <span className="shrink-0 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-medium text-amber-800">

                            draft

                          </span>

                        )}

                      </div>

                      <p className="mt-0.5 text-[10px] text-slate-500 truncate">

                        {rule.transaction} · {rule.design?.stages?.length || 0} stage

                        {(rule.design?.stages?.length || 0) === 1 ? '' : 's'}

                      </p>

                    </div>

                    <ChevronRight

                      className={`mt-1 h-4 w-4 shrink-0 ${active ? 'text-cyan-700' : 'text-slate-300'}`}

                    />

                  </button>

                </li>

              );

            })

          )}

        </ul>



      </aside>



      {/* Right: workflow canvas */}

      <section className="flex min-h-[420px] min-w-0 flex-1 flex-col">

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-white px-4 py-2">

          <div className="min-w-0">

            <p className="truncate text-sm font-semibold text-slate-800">

              {selectedRule ? selectedRule.name : 'No rule selected'}

            </p>

          </div>

          <div className="flex flex-wrap items-center gap-1.5">

            {statusBadge && (

              <span

                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${statusBadge.className}`}

              >

                {statusBadge.label}

              </span>

            )}

            {saveState === 'saving' && (

              <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">

                <Loader2 className="h-3 w-3 animate-spin" /> Saving draft…

              </span>

            )}

            {saveState === 'saved' && (

              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700">

                <CheckCircle2 className="h-3 w-3" /> Draft saved

              </span>

            )}

          </div>

        </div>



        {selectedRule && !readOnly && (

          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-2">

            <button

              type="button"

              onClick={handleManualSave}

              disabled={!draftsEnabled || saveState === 'saving'}

              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"

              title={draftsEnabled ? 'Save draft to MongoDB' : 'Set MONGODB_URI to enable drafts'}

            >

              <Save className="h-3.5 w-3.5" />

              Save draft

            </button>

            <button

              type="button"

              onClick={handleCompare}

              disabled={!draftsEnabled || !selectedRule.mongoId || compareLoading}

              className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-white px-2.5 py-1 text-[11px] font-medium text-violet-900 hover:bg-violet-50 disabled:opacity-50"

            >

              {compareLoading ? (

                <Loader2 className="h-3.5 w-3.5 animate-spin" />

              ) : (

                <GitCompare className="h-3.5 w-3.5" />

              )}

              Compare changes

            </button>

            <button

              type="button"

              onClick={handleSimulate}

              disabled={!draftsEnabled || !selectedRule.mongoId || simulateLoading}

              className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-white px-2.5 py-1 text-[11px] font-medium text-emerald-900 hover:bg-emerald-50 disabled:opacity-50"

            >

              {simulateLoading ? (

                <Loader2 className="h-3.5 w-3.5 animate-spin" />

              ) : (

                <Play className="h-3.5 w-3.5" />

              )}

              Run simulation

            </button>

            <button

              type="button"

              onClick={() => setShowDeployConfirm(true)}

              disabled={!draftsEnabled || !selectedRule.mongoId || deployLoading}

              className="inline-flex items-center gap-1 rounded-lg bg-cyan-800 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-cyan-900 disabled:opacity-50"

            >

              {deployLoading ? (

                <Loader2 className="h-3.5 w-3.5 animate-spin" />

              ) : (

                <Rocket className="h-3.5 w-3.5" />

              )}

              Deploy to Oracle HCM

            </button>

          </div>

        )}



        {draftError && (

          <div className="flex shrink-0 items-start gap-2 border-b border-rose-100 bg-rose-50 px-4 py-2 text-xs text-rose-800">

            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />

            <span>{draftError}</span>

          </div>

        )}



        {compareResult && (

          <div className="shrink-0 border-b border-violet-100 bg-violet-50/60 px-4 py-2 text-xs space-y-1">

            <p className="font-medium text-violet-900">{compareResult.summary}</p>

            {(compareResult.changes || []).map((c, i) => (

              <p key={i} className="text-slate-700">

                <span className="font-medium">{c.field}</span>

                {c.detail ? `: ${c.detail}` : ''}

              </p>

            ))}

          </div>

        )}



        <div className="relative min-h-0 flex-1">

          <ApprovalDesignerCanvas

            design={canvasDesign}

            onDesignChange={handleRuleDesignChange}

            readOnly={readOnly}

          />

        </div>

      </section>



      {showDeployConfirm && (

        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">

          <div

            className="w-full max-w-md rounded-xl border border-cyan-200 bg-white p-5 shadow-xl"

            role="dialog"

            aria-labelledby="deploy-confirm-title"

          >

            <h3 id="deploy-confirm-title" className="text-sm font-semibold text-slate-900">

              Deploy to Oracle HCM?

            </h3>

            <p className="mt-2 text-xs text-slate-600">

              Pushes this draft to Fusion. Use dry-run on Dev first.

            </p>

            <p className="mt-1 text-xs font-medium text-slate-800 truncate">{selectedRule?.name}</p>

            <div className="mt-4 flex flex-wrap justify-end gap-2">

              <button

                type="button"

                onClick={() => setShowDeployConfirm(false)}

                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"

              >

                Cancel

              </button>

              <button

                type="button"

                onClick={() => handleDeploy(true)}

                disabled={deployLoading}

                className="rounded-lg border border-cyan-300 px-3 py-1.5 text-xs font-medium text-cyan-900 hover:bg-cyan-50 disabled:opacity-50"

              >

                Dry-run only

              </button>

              <button

                type="button"

                onClick={() => handleDeploy(false)}

                disabled={deployLoading}

                className="rounded-lg bg-cyan-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-900 disabled:opacity-50"

              >

                Deploy now

              </button>

            </div>

          </div>

        </div>

      )}

    </div>

  );

}


